import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Eye, EyeOff } from 'lucide-react';
import { signIn, signInWithToken, signOut } from '@/utils/firebase';
import { AuthError } from '@/utils/errors';
import { useToast } from '@/contexts/toast-context';
import { login, driverLogin } from '@/api/user.api';
import { useAuth, UserProfile } from '@/hooks/use-auth';
import { setCachedProfile } from '@/utils/profile-cache';
import { Role } from '@/constants/roles.constants';
import {
  initializeFCMForUser,
  cleanupFCMTokenForDevice,
  PUSH_ROLES,
} from '@/lib/fcm/init';
import userIsAuthorized from '@/utils/userIsAuthorized';

import { Card, CardContent, Button, Label, Input } from '@/components/ui';
import { Spinner, AuthFooterLinks } from '../../components';

import './login-style.scss';
import { TOAST_TYPES } from '@/constants/common.constants';

type LoginMode = 'email' | 'driver';

/**
 * Remembered per device. The PWA opens at "/" with no query string, so without this a
 * driver would land on the email form every time they reopen the installed app — and a
 * PWA has no address bar to correct it with.
 */
const LOGIN_MODE_KEY = 'qluu.loginMode';

const readStoredMode = (): LoginMode | null => {
  try {
    const v = localStorage.getItem(LOGIN_MODE_KEY);
    return v === 'driver' || v === 'email' ? v : null;
  } catch {
    return null;
  }
};

const storeMode = (mode: LoginMode) => {
  try {
    localStorage.setItem(LOGIN_MODE_KEY, mode);
  } catch {
    // Private browsing or blocked storage — the toggle still works for this session.
  }
};

export const Route = createFileRoute('/login/')({
  component: Login,
  validateSearch: (
    search: Record<string, unknown>,
  ): { 'redirect-url'?: string; mode?: string } => {
    return {
      'redirect-url': (search['redirect-url'] as string) ?? undefined,
      mode: (search['mode'] as string) ?? undefined,
    };
  },
});

function Login() {
  const { mode: modeParam } = Route.useSearch();

  // Precedence: an explicit ?mode= link, then whatever this device used last, then
  // Driver ID. Drivers vastly outnumber office users, so they are the better default for
  // someone arriving with no stored preference; anyone else switches once and it sticks.
  const [mode, setMode] = useState<LoginMode>(
    modeParam === 'driver'
      ? 'driver'
      : modeParam === 'email'
      ? 'email'
      : readStoredMode() ?? 'driver',
  );

  const [email, setEmail] = useState('');
  const [driverId, setDriverId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    identifier?: string;
    password?: string;
  }>({});

  const { setUser, setIsAuthenticated, setSkipFetch } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const isDriverMode = mode === 'driver';
  const identifier = isDriverMode ? driverId : email;

  const switchMode = (next: LoginMode) => {
    setMode(next);
    storeMode(next);
    setErrors({});
    setPassword('');
  };

  /** Shared tail of both flows: profile fetch, context, then routing. */
  const completeSignIn = async () => {
    const response = await login();
    const userData: UserProfile = {
      ...response.user,
      role: response.user.role as Role,
    };

    setUser(userData);
    setIsAuthenticated(true);
    setCachedProfile(userData);

    // Still on a temporary password — nothing else in the app is reachable until they
    // set their own, and the server enforces that independently of this redirect.
    if (userData.mustSetPassword) {
      navigate({ to: '/set-password' });
      return;
    }

    if (userIsAuthorized(userData.role, PUSH_ROLES)) {
      initializeFCMForUser();
    } else {
      cleanupFCMTokenForDevice();
    }

    const urlParams = new URLSearchParams(window.location.search);
    const redirectUrl = urlParams.get('redirect-url') || '/dashboard';
    const [pathname, search] = redirectUrl.split('?');
    if (search) {
      const searchParams = Object.fromEntries(new URLSearchParams(search));
      navigate({ to: pathname, search: searchParams });
    } else {
      navigate({ to: pathname });
    }
  };

  const handleSubmit = async () => {
    const newErrors: typeof errors = {};
    if (!identifier) {
      newErrors.identifier = isDriverMode
        ? 'Driver ID is required'
        : 'Email address is required';
    }
    if (!password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setIsLoading(true);
      setSkipFetch(true);

      if (isDriverMode) {
        // Firebase cannot verify a password for a user with no email, so the server
        // checks the credential and returns a custom token to exchange for a session.
        const { token } = await driverLogin(driverId, password);
        await signInWithToken(token);
        storeMode('driver');
      } else {
        await signIn(email, password);
      }

      await completeSignIn();
    } catch (err: unknown) {
      setIsLoading(false);
      await signOut();
      if (err instanceof AuthError) {
        addToast(err.message, { type: TOAST_TYPES.ERROR });
      } else if (err instanceof Error) {
        addToast(err.message, { type: TOAST_TYPES.ERROR });
      } else {
        addToast('An unexpected error occurred. Please try again.', {
          type: TOAST_TYPES.ERROR,
        });
      }
    }
  };

  return (
    <div className="login-container">
      <div className="login-content">
        <Card className="login-card">
          <CardContent>
            <div className="login-header">
              <img src="/full-logo.svg" alt="Logo" className="login-logo" />
              <h1>Sign in to your account</h1>
            </div>

            {/* Identifier — email address, or Driver ID on the Driver ID channel */}
            <div className="login-field">
              <Label>{isDriverMode ? 'Driver ID' : 'Email address'}</Label>
              <Input
                value={identifier}
                type={isDriverMode ? 'text' : 'email'}
                autoCapitalize={isDriverMode ? 'characters' : 'none'}
                autoComplete={isDriverMode ? 'username' : 'email'}
                onChange={(e) => {
                  if (isDriverMode) setDriverId(e.target.value);
                  else setEmail(e.target.value);
                  if (errors.identifier) {
                    setErrors((prev) => ({ ...prev, identifier: undefined }));
                  }
                }}
              />
              {errors.identifier && (
                <p className="invalid-feedback">{errors.identifier}</p>
              )}
            </div>

            {/* Password */}
            <div className="login-field">
              <Label>Password</Label>
              <div className="login-password-wrapper">
                <Input
                  value={password}
                  type={showPassword ? 'text' : 'password'}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) {
                      setErrors((prev) => ({ ...prev, password: undefined }));
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                />

                <button
                  type="button"
                  className="basic-primary login-password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {errors.password && (
                <p className="invalid-feedback">{errors.password}</p>
              )}
            </div>

            <div className=" pb-4">
              {isDriverMode ? (
                // Drivers have no email, so there is no self-service recovery — a
                // manager resets their password from the driver's edit page.
                <p className="login-help-text">
                  Forgot password? Please contact your manager.
                </p>
              ) : (
                <Button
                  variant="link-primary"
                  onClick={() => navigate({ to: '/forgot-password' })}
                >
                  Forgot Password?
                </Button>
              )}
            </div>

            <Button
              variant="basic-primary"
              className="login-submit-button"
              size="medium"
              onClick={handleSubmit}
            >
              {isLoading && <Spinner size="small" />}
              Sign in
            </Button>

            {/* The escape hatch. Without it a driver who lands on the email form inside
                the installed PWA has no address bar to correct it with. */}
            <div className="login-mode-switch">
              <Button
                variant="link-primary"
                onClick={() => switchMode(isDriverMode ? 'email' : 'driver')}
              >
                {isDriverMode
                  ? 'Sign in with email instead'
                  : 'Sign in with Driver ID'}
              </Button>
            </div>
          </CardContent>
        </Card>
        <AuthFooterLinks />
      </div>
    </div>
  );
}
